/**
 * Rate Limit Service
 *
 * Handles request queuing, retry logic, and coordination with the store
 */

import axios from "axios";
import store from "@/store";

class RateLimitService {
	constructor() {
		this.queue = [];
		this.processing = false;
		this.retryTimer = null;
		this.maxRetries = 3;
		this.retryDelay = 1000; // Base delay in ms

		// Listen for retry events from the store
		window.addEventListener("rate-limit-retry", this.handleRetryEvent.bind(this));
	}

	/**
	 * Queue a request for retry
	 * @param {Object} config - Axios request config
	 * @returns {Promise} - Resolves when request succeeds or fails permanently
	 */
	queueRequest(config) {
		return new Promise((resolve, reject) => {
			const request = {
				config,
				resolve,
				reject,
				retries: 0,
				id: Date.now() + Math.random(),
			};

			this.queue.push(request);

			// If not rate limited, process immediately
			if (!store.state.rateLimiting.isRateLimited) {
				this.processQueue();
			}
		});
	}

	/**
	 * Process queued requests
	 */
	async processQueue() {
		if (this.processing || this.queue.length === 0) return;

		// Check if still rate limited
		const { isRateLimited, resetTimestamp } = store.state.rateLimiting;

		if (isRateLimited) {
			const waitTime = resetTimestamp - Date.now();
			if (waitTime > 0) {
				// Schedule retry after rate limit expires
				this.scheduleRetry(waitTime);
				return;
			}
		}

		this.processing = true;
		const request = this.queue.shift();

		try {
			// Add retry headers
			if (!request.config.headers) {
				request.config.headers = {};
			}
			request.config.headers["X-Retry-Count"] = request.retries;

			// Make the request
			const response = await axios(request.config);

			// Check for rate limit headers in successful response
			this.checkRateLimitHeaders(response);

			// Resolve the promise
			request.resolve(response);
		} catch (error) {
			if (error.response?.status === 429) {
				// Put request back in queue
				request.retries++;

				if (request.retries < this.maxRetries) {
					this.queue.unshift(request);

					// Extract retry-after header
					const retryAfter =
						error.response.headers["retry-after"] ||
						error.response.headers["x-ratelimit-retry-after"] ||
						60;

					// Extract lockout information
					const isLockedOut =
						error.response.headers["x-ratelimit-lockout"] === "true" ||
						error.response.data?.isLockedOut === true;
					const lockoutDuration =
						error.response.headers["x-ratelimit-lockout-duration"] ||
						error.response.data?.lockoutDuration;

					// Update store with rate limit info
					store.dispatch("rateLimiting/handleRateLimited", {
						retryAfter: parseInt(retryAfter),
						message: this.extractErrorMessage(error),
						isLockedOut,
						lockoutDuration: lockoutDuration ? parseInt(lockoutDuration) : null,
						violatedLimit: error.response.data?.violatedLimit,
					});
				} else {
					// Max retries exceeded
					request.reject(new Error("Maximum retry attempts exceeded for rate limited request"));
				}
			} else {
				// Non-rate limit error
				request.reject(error);
			}
		} finally {
			this.processing = false;

			// Process next request if available
			if (this.queue.length > 0 && !store.state.rateLimiting.isRateLimited) {
				setTimeout(() => this.processQueue(), 100);
			}
		}
	}

	/**
	 * Schedule a retry after delay
	 * @param {number} delay - Delay in milliseconds
	 */
	scheduleRetry(delay) {
		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
		}

		this.retryTimer = setTimeout(() => {
			store.commit("rateLimiting/SET_RATE_LIMITED", { limited: false });
			this.processQueue();
		}, delay);
	}

	/**
	 * Check response headers for rate limit information
	 * @param {Object} response - Axios response object
	 */
	checkRateLimitHeaders(response) {
		const headers = response.headers;

		// Common rate limit header patterns
		const remaining =
			headers["x-ratelimit-remaining"] ||
			headers["x-rate-limit-remaining"] ||
			headers["ratelimit-remaining"];

		const limit = headers["x-ratelimit-limit"] || headers["x-rate-limit-limit"] || headers["ratelimit-limit"];

		if (remaining !== undefined) {
			store.dispatch("rateLimiting/updateLimits", {
				remaining: parseInt(remaining),
				limit: limit ? parseInt(limit) : null,
			});
		}
	}

	/**
	 * Extract error message from rate limit response
	 * @param {Object} error - Error object
	 * @returns {string} - Error message
	 */
	extractErrorMessage(error) {
		if (error.response?.data?.message) {
			return error.response.data.message;
		}
		if (error.response?.data?.error) {
			return error.response.data.error;
		}
		if (error.response?.statusText) {
			return `Request limit exceeded: ${error.response.statusText}`;
		}
		return "You have exceeded the maximum number of requests. Please wait before trying again.";
	}

	/**
	 * Handle retry event from store
	 * @param {CustomEvent} event - Custom event with request details
	 */
	handleRetryEvent(event) {
		const request = event.detail;
		if (request && request.config) {
			this.queueRequest(request.config);
		}
	}

	/**
	 * Clear all queued requests
	 */
	clearQueue() {
		this.queue.forEach((request) => {
			request.reject(new Error("Request cancelled due to queue clear"));
		});
		this.queue = [];

		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = null;
		}
	}

	/**
	 * Get queue status
	 * @returns {Object} - Queue status information
	 */
	getQueueStatus() {
		return {
			queueLength: this.queue.length,
			processing: this.processing,
			isRateLimited: store.state.rateLimiting.isRateLimited,
		};
	}
}

// Create singleton instance
const rateLimitService = new RateLimitService();

// Export for use in other modules
export default rateLimitService;
